"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";

const NAVBAR_LINE_COLOR = "#2e1065";
const CAROUSEL_INTERVAL_MS = 5000;
const SLIDE_DURATION_MS = 400;

export interface WPhotoStripeSectionProps {
  heading?: string;
  showTitle?: boolean;
  images?: Array<{ imageUrl?: string; alt?: string }>;
}

export default function WPhotoStripeSection({
  heading,
  showTitle = true,
  images = [],
}: WPhotoStripeSectionProps) {
  const displayTitle =
    heading != null && String(heading).trim() !== "" ? String(heading).trim() : "";
  const showTitleBlock = showTitle !== false && displayTitle !== "";

  const items = (images || []).filter(
    (img) => img?.imageUrl && String(img.imageUrl).trim() !== ""
  );
  const length = items.length;
  // Duplicate items for infinite loop (no reverse animation on wrap)
  const trackItems = length > 0 ? [...items, ...items] : [];
  const trackLength = trackItems.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);

  const goNext = useCallback(() => {
    if (length <= 1) return;
    if (currentIndex < trackLength - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setSuppressTransition(true);
      setCurrentIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSuppressTransition(false);
        });
      });
    }
  }, [length, trackLength, currentIndex]);

  const goPrev = useCallback(() => {
    if (length <= 1) return;
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else {
      setSuppressTransition(true);
      setCurrentIndex(length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSuppressTransition(false);
          setCurrentIndex(length - 1);
        });
      });
    }
  }, [length, currentIndex]);

  useEffect(() => {
    if (length <= 1) return;
    const id = setInterval(goNext, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [length, goNext]);

  const hasImages = length > 0;
  const displayIndex = trackLength > 0 ? (currentIndex % length) + 1 : 0;

  return (
    <section className="w-full py-6 md:py-8">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        {showTitleBlock && (
          <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${NAVBAR_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
              {displayTitle}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${NAVBAR_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}

        {/* Viewport: 2 images on mobile, 3 on desktop */}
        <div className="relative h-[180px] w-full overflow-hidden rounded-xl md:h-[215px]">
          <div
            className={`w-photo-stripe-track flex h-full flex-nowrap gap-6 ${suppressTransition ? "no-transition" : ""}`}
            style={{ "--photo-index": hasImages ? currentIndex : 0 } as React.CSSProperties}
          >
            {hasImages ? trackItems.map((item, index) => (
              <div
                key={`${index}-${item.imageUrl}`}
                className="w-photo-stripe-slot relative flex h-[180px] flex-shrink-0 md:h-[215px]"
              >
                <div className="relative h-full w-full">
                  <Image
                    src={item.imageUrl!}
                    alt={item.alt || `Photo ${(index % length) + 1}`}
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 50vw, 33vw"
                    unoptimized={
                      item.imageUrl!.startsWith("http") &&
                      !item.imageUrl!.includes("trichos")
                    }
                  />
                </div>
              </div>
            )) : (
              <div className="flex w-full items-center justify-center rounded-xl bg-gray-100 py-16 text-center text-gray-500">
                <p>No photos yet. Add photos in the W-Photo Stripe section in Sanity.</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom: left arrow, 1/10, right arrow */}
        {hasImages && (
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={goPrev}
            className="flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-40"
            aria-label="Previous photo"
            disabled={length <= 1}
          >
            <svg
              className="size-8 md:size-9"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span
            className="min-w-[3rem] text-center text-sm font-medium text-gray-700 md:text-base"
            aria-live="polite"
          >
            {displayIndex}/{length}
          </span>
          <button
            type="button"
            onClick={goNext}
            className="flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-40"
            aria-label="Next photo"
            disabled={length <= 1}
          >
            <svg
              className="size-8 md:size-9"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        )}
      </div>
    </section>
  );
}
